import {
  type GremlinQueryParams,
  type PaginatedQueryParams
} from 'queries/queries-utils'

import type {
  MetricsAwsTargetsRequest,
  ServiceDependencyParams,
  UpdateServiceQueryKeyParams
} from 'queries/services/types'

interface WithTeamId {
  teamId: string
}

interface ServiceIdWithTeamId extends WithTeamId {
  serviceId: string
}

export const serviceQueryKeys = {
  getAllForCompany: (params: PaginatedQueryParams) =>
    ['services/all-for-company', params] as const,
  getById: ({ serviceId, teamId }: ServiceIdWithTeamId) =>
    [`services/${serviceId}`, { teamId }] as const,
  getDependencies: ({ serviceId, ...params }: ServiceIdWithTeamId) =>
    [`/services/${serviceId}/dependencies`, params] as const,
  getIntelligentHealthChecks: ({ serviceId, ...params }: ServiceIdWithTeamId) =>
    [`services/${serviceId}/intelligent-health-checks`, params] as const,
  getMetricsAwsTargets: ({
    awsResource,
    serviceId,
    ...queryParams
  }: MetricsAwsTargetsRequest) =>
    [`services/${serviceId}/metrics/${awsResource}`, queryParams] as const,
  getServices: (params: { teamId: string; pageSize: number }) =>
    ['services', params] as const,
  getStatusChecks: ({ serviceId, ...params }: ServiceIdWithTeamId) =>
    [`services/${serviceId}/status-checks`, params] as const,
  getTestSeriesRunsActive: ({ serviceId, ...params }: ServiceIdWithTeamId) =>
    [`services/${serviceId}/test-series-runs/active`, params] as const
}

export const serviceMutationKeys = {
  createService: (params: Required<GremlinQueryParams>) =>
    ['services', params] as const,
  createDependency: ({ serviceId, ...params }: ServiceIdWithTeamId) =>
    [`services/${serviceId}/dependencies`, params] as const,
  editDependency: ({
    dependencyId,
    serviceId,
    ...params
  }: ServiceDependencyParams & WithTeamId) =>
    [`services/${serviceId}/dependencies/${dependencyId}`, params] as const,
  deleteDependency: ({
    dependencyId,
    serviceId,
    ...params
  }: ServiceDependencyParams & WithTeamId) =>
    [`/services/${serviceId}/dependencies/${dependencyId}`, params] as const,
  deleteDiscoveredDependencies: ({
    serviceId,
    ...params
  }: { confirm: boolean } & ServiceIdWithTeamId) =>
    [`/services/${serviceId}/dependencies/discovered`, params] as const,
  updateService: ({ serviceId, ...params }: UpdateServiceQueryKeyParams) =>
    [`services/${serviceId}`, params] as const
}
